import { IBackend } from '@/api/BackendApi';
import { extractNodeType, extractNumericEntityIdFromDataId } from '@/lib/tree/treeUtils';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { NodeDataType, NodeType } from '@/types/tree';
import { log } from '@/lib/utils/logger';
import {
  ISubmitDependencies,
  SampleActivitySaveIncompleteError,
  updateSampleActivityWithPreconditionRetry,
} from '@/lib/tree/submitEntityUtils';
import {
  SampleDto,
  DataPackageDto,
  PublicationDto,
  UpdateSampleActivityRequest,
  UpdateProjectActivityRequest,
  UpdateTheoryActivityRequest,
  UpdatePublicationRequest,
  CommunicationDto,
  ShippingDto,
  ShippingRequest,
  SampleWithLabel,
  SampleRequestWithDelivery,
  UpdateDataPackageRequest,
  CreateOrUpdateProjectRequestDto,
} from '@/api/ApiClient';
import { buildProjectCollaborators, ProjectUIModel, toProjectUIModel } from '@/lib/models/projectUIModel';
import { toUpdateCommunicationRequest } from '@/lib/entity/communicationRequests';

/**
 * Builds the `UpdateDataPackageRequest` payload expected by `backend.data.update`.
 * Keep this mapping centralized so manual save and autosave send identical fields.
 */
export function toUpdateDataPackageRequest(dataPackage: DataPackageDto): UpdateDataPackageRequest {
  return {
    id: dataPackage.id!,
    label: dataPackage.label || '',
    collaboratorIds: dataPackage.collaborators?.map((c) => c.id).filter((id): id is string => !!id) || [],
    synthesisInstrumentIds: dataPackage.synthesisInstruments?.map((s) => s.id).filter((id): id is string => !!id) || [],
    characterizationInstrumentIds:
      dataPackage.characterizationInstruments?.map((c) => c.id).filter((id): id is string => !!id) || [],
    description: dataPackage.description || '',
    keywords: dataPackage.keywords,
  };
}

/**
 * Builds the simulation-dataset update payload used by `backend.data.update`.
 * Keep this separate from data package mapping because simulation dataset saves a different field set.
 */
export function toUpdateSimulationDatasetRequest(dataPackage: DataPackageDto) {
  return {
    id: dataPackage.id!,
    label: dataPackage.label || '',
    description: dataPackage.description || '',
    doi: dataPackage.doi,
    publicationId: dataPackage.publicationId,
  };
}

/**
 * Builds the `CreateOrUpdateProjectRequestDto` payload expected by `backend.project.update`.
 * We store `ProjectUIModel` in tree state, but API expects project `type` as code string.
 */
export function toUpdateProjectRequest(
  project: ProjectUIModel,
  options?: { setAwardGeography?: boolean }
): CreateOrUpdateProjectRequestDto {
  const setAwardGeography = options?.setAwardGeography ?? false;
  return {
    id: project.id,
    projectLabel: project.projectLabel || '',
    type: project.type?.code,
    title: project.title,
    startDate: project.startDate,
    endDate: project.endDate,
    category: project.type?.isInternal ? undefined : project.category,
    projectStatus: project.projectStatus,
    isPublished: project.isPublished || false,
    lastChangedDate: project.lastChangedDate,
    lastChangedByUser: project.lastChangedByUser,
    materialIds: project.materialIds || [],
    materials: project.materials || [],
    techniques: project.techniques || [],
    collaborators: buildProjectCollaborators(project).map((c) => ({
      id: c.id || '',
      roleId: c.roleId || 0,
    })),
    ...(setAwardGeography
      ? {
          setAwardGeography: true,
          piInstitution: project.piInstitution?.trim(),
          piState: project.piState?.trim(),
          category: project.category,
        }
      : {}),
  };
}

/**
 * Builds the `UpdatePublicationRequest` payload expected by `backend.publication.update`.
 * Keep trimming/ID extraction centralized so manual save and autosave stay identical.
 */
export function toUpdatePublicationRequest(publication: PublicationDto): UpdatePublicationRequest {
  return {
    id: publication.id!,
    title: (publication.title ?? '').trim(),
    year: publication.year,
    type: publication.type as string,
    authors: (publication.authors ?? '').trim(),
    journal: (publication.journal ?? '').trim(),
    cite: (publication.cite ?? '').trim(),
    doi: (publication.doi ?? '').trim(),
    description: (publication.description ?? '').trim(),
    scienceDriverIds: publication.scienceDriverIds ?? [],
    materialIds: publication.materials?.map((m) => m.id!).filter((id): id is number => id !== undefined) ?? [],
    projectIds: publication.projects?.map((p) => p.id!).filter((id): id is number => id !== undefined) ?? [],
    synthesisInstrumentIds:
      publication.synthesisInstruments?.map((i) => i.id!).filter((id): id is string => !!id) ?? [],
    characterizationInstrumentIds:
      publication.characterizationInstruments?.map((i) => i.id!).filter((id): id is string => !!id) ?? [],
  };
}

/**
 * Unified function to save/update an entity based on its type.
 * Read-only node types (package browse, snapshot, aggregate) are rejected — no live-API fallthrough.
 *
 * @param type - The node type (e.g., 'sample', 'project', 'sampleActivity')
 * @param dataId - The composite data ID (e.g., "sample-ABC123" or "sampleActivity-123")
 * @param data - The entity data to save
 * @param backend - The backend API instance
 * @param sampleActivityPreconditionDeps - When saving a sample activity, pass UI deps so 428 matches submit (confirm + retry with userHasConfirmed)
 * @returns The updated entity data from the server
 */
export async function saveEntity(
  type: NodeType,
  dataId: string,
  data: NodeDataType,
  backend: IBackend,
  sampleActivityPreconditionDeps?: ISubmitDependencies
): Promise<NodeDataType> {
  if (nodeIsReadOnlyForUi(type)) {
    log.warn('[EntitySaveUtils] Refusing save for read-only node type:', type);
    throw new Error(`Save not supported for read-only node type: ${type}`);
  }

  switch (type) {
    case 'sample':
      return await backend.sample.update((data as SampleDto).sampleLabel!, data as SampleDto);

    case 'sampleActivity': {
      const activityId = extractNumericEntityIdFromDataId(dataId);
      const activityRequest = data as UpdateSampleActivityRequest;
      if (sampleActivityPreconditionDeps) {
        const updated = await updateSampleActivityWithPreconditionRetry(
          backend,
          activityId,
          activityRequest,
          sampleActivityPreconditionDeps
        );
        if (updated === null) {
          throw new SampleActivitySaveIncompleteError();
        }
        return updated;
      }
      return await backend.sampleActivity.update(activityId, undefined, activityRequest);
    }

    case 'project':
      return toProjectUIModel(
        await backend.project.update((data as ProjectUIModel).id!, toUpdateProjectRequest(data as ProjectUIModel))
      );

    case 'projectActivity':
      const projectActivityId = extractNumericEntityIdFromDataId(dataId);
      return await backend.projectActivity.update(projectActivityId, data as UpdateProjectActivityRequest);

    case 'theoryActivity':
      const theoryActivityId = extractNumericEntityIdFromDataId(dataId);
      return await backend.theoryActivity.update(theoryActivityId, data as UpdateTheoryActivityRequest);

    case 'dataPackage':
      const dataPackageId = extractNumericEntityIdFromDataId(dataId);
      return await backend.data.update(dataPackageId.toString(), toUpdateDataPackageRequest(data as DataPackageDto));

    case 'simulationDataset':
      const simulationDatasetId = extractNumericEntityIdFromDataId(dataId);
      return await backend.data.update(
        simulationDatasetId.toString(),
        toUpdateSimulationDatasetRequest(data as DataPackageDto)
      );

    case 'communication':
      const communicationId = extractNumericEntityIdFromDataId(dataId);
      return await backend.communication.update(
        communicationId,
        toUpdateCommunicationRequest(data as CommunicationDto)
      );

    case 'publication':
      const publicationId = extractNumericEntityIdFromDataId(dataId);
      return await backend.publication.update(
        publicationId.toString(),
        toUpdatePublicationRequest(data as PublicationDto)
      );

    case 'shipping':
      const shippingId = extractNumericEntityIdFromDataId(dataId);
      // Convert ShippingDto to ShippingRequest (strip out full sample/request objects)
      const shippingDto = data as ShippingDto;
      const shippingRequest: ShippingRequest = {
        projectId: shippingDto.projectId,
        researcher: shippingDto.researcher,
        researcherPhone: shippingDto.researcherPhone,
        researcherEmail: shippingDto.researcherEmail,
        address: shippingDto.address,
        returnAddress: shippingDto.returnAddress,
        date: shippingDto.date,
        budget: shippingDto.budget,
        contactName: shippingDto.contactName,
        contactEmail: shippingDto.contactEmail,
        contactPhone: shippingDto.contactPhone,
        localTransfer: shippingDto.localTransfer,
        shippingType: shippingDto.shippingType,
        trackingID: shippingDto.trackingID,
        status: shippingDto.status,
        samples: (shippingDto.samples || []).map((s): SampleWithLabel => ({
          sampleId: s.sampleId,
          labelType: s.labelType,
        })),
        sampleRequests: (shippingDto.sampleRequests || []).map((r): SampleRequestWithDelivery => ({
          requestId: r.requestId,
          partialDelivery: r.partialDelivery ?? false,
        })),
      };
      return await backend.shipping.update(shippingId, shippingRequest);

    default:
      log.warn('[EntitySaveUtils] Unsupported node type for save:', type);
      throw new Error(`Save not supported for node type: ${type}`);
  }
}

/**
 * Convenience function to save an entity by dataId
 * Extracts the type from the dataId automatically
 *
 * @param dataId - The composite data ID (e.g., "sample-ABC123")
 * @param data - The entity data to save
 * @param backend - The backend API instance
 * @returns The updated entity data from the server
 */
export async function saveEntityByDataId(
  dataId: string,
  data: NodeDataType,
  backend: IBackend,
  sampleActivityPreconditionDeps?: ISubmitDependencies
): Promise<NodeDataType> {
  const type = extractNodeType(dataId);
  if (!type) {
    throw new Error(`Could not extract type from dataId: ${dataId}`);
  }
  return saveEntity(type, dataId, data, backend, sampleActivityPreconditionDeps);
}
